import { useParams, Link } from 'react-router-dom';
import { Tag, Typography } from 'antd';

import { useEmployeeMedicalCard } from '@/entities/medical/api/medicalChecksApi';
import { admissionStatusLabels, type AdmissionStatus } from '@/entities/medical/model/medicalChecksSchema';
import {
  medicalAdmissionTagColor,
  medicalDecisionOrAdmissionLabel,
} from '@/entities/medical/model/medicalCheckTableTags';

import { PageSpinner } from '@/shared/ui/PageSpinner';
import { MedicalEmployeeCardBody } from './MedicalEmployeeCardBody';
import s from './EmployeeMedicalCard.module.scss';

const { Title } = Typography;

export type MedicalEmployeeDetailProps = {
  employeeId: string;
  showBack?: boolean;
  hideHero?: boolean;
  embeddedInEmployeeCard?: boolean;
  onOpenAddMedicalRecord?: () => void;
};

export function MedicalEmployeeDetail({
  employeeId,
  showBack = true,
  hideHero,
  embeddedInEmployeeCard,
  onOpenAddMedicalRecord,
}: MedicalEmployeeDetailProps) {
  const { data, isLoading } = useEmployeeMedicalCard(employeeId);

  if (isLoading) {
    return <PageSpinner />;
  }

  if (!data) {
    return (
      <div className={s.root}>
        {showBack ? (
          <div className={s.root__breadcrumbs}>
            <Link to="/medical?tab=employees" className={s.root__crumbLink}>
              ← К списку медосмотров
            </Link>
          </div>
        ) : null}
        <Title level={4}>Сотрудник не найден</Title>
      </div>
    );
  }

  const { employee } = data;
  const employeeCardUrl = `/employees?employeeId=${employeeId}&tab=medical`;

  return (
    <div className={s.root}>
      {showBack ? (
        <div className={s.root__breadcrumbs}>
          <Link to="/medical?tab=employees" className={s.root__crumbLink}>
            ← К списку медосмотров
          </Link>
          <span className={s.root__crumbSep}>/</span>
          <span>{employee.full_name}</span>
        </div>
      ) : null}

      {!hideHero ? (
        embeddedInEmployeeCard ? (
          <div className={s.root__hero}>
            <div className={s.root__heroAvatar}>
              {employee.full_name
                .split(' ')
                .slice(0, 2)
                .map((n) => n[0])
                .join('')}
            </div>
            <div className={s.root__heroInfo}>
              <div className={s.root__heroName} data-hero-name>
                {employee.full_name}
              </div>
              <div className={s.root__heroMeta}>{employee.position?.name ?? '—'}</div>
              <div className={s.root__heroMeta}>{employee.org_unit.name}</div>
              <div className={s.root__heroBadges}>
                {employee.tab_number ? <Tag className={s.root__heroTagDark}>Таб. {employee.tab_number}</Tag> : null}
                <Tag
                  color={medicalAdmissionTagColor[employee.admission_indicator] ?? 'default'}
                  className={s.root__heroStatusTag}
                >
                  {medicalDecisionOrAdmissionLabel(employee.admission_indicator) ||
                    admissionStatusLabels[employee.admission_indicator as AdmissionStatus] ||
                    employee.admission_indicator}
                </Tag>
              </div>
            </div>
          </div>
        ) : (
          <Link to={employeeCardUrl} className={s.root__heroLink}>
            <div className={s.root__hero}>
              <div className={s.root__heroAvatar}>
                {employee.full_name
                  .split(' ')
                  .slice(0, 2)
                  .map((n) => n[0])
                  .join('')}
              </div>
              <div className={s.root__heroInfo}>
                <div className={s.root__heroName} data-hero-name>
                  {employee.full_name}
                </div>
                <div className={s.root__heroMeta}>{employee.position?.name ?? '—'}</div>
                <div className={s.root__heroMeta}>{employee.org_unit.name}</div>
                <div className={s.root__heroBadges}>
                  {employee.tab_number ? <Tag className={s.root__heroTagDark}>Таб. {employee.tab_number}</Tag> : null}
                  <Tag
                    color={medicalAdmissionTagColor[employee.admission_indicator] ?? 'default'}
                    className={s.root__heroStatusTag}
                  >
                    {medicalDecisionOrAdmissionLabel(employee.admission_indicator) ||
                      admissionStatusLabels[employee.admission_indicator as AdmissionStatus] ||
                      employee.admission_indicator}
                  </Tag>
                </div>
              </div>
            </div>
          </Link>
        )
      ) : null}

      <MedicalEmployeeCardBody data={data} onOpenAddMedicalRecord={onOpenAddMedicalRecord} />
    </div>
  );
}

export function EmployeeMedicalCard() {
  const { employeeId } = useParams<{ employeeId: string }>();
  if (!employeeId) {
    return null;
  }
  return <MedicalEmployeeDetail employeeId={employeeId} showBack />;
}
